<template>
  <div class="return-order-form">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ isEdit ? '编辑返货单' : '新增返货单' }}</span>
          <div class="card-actions">
            <el-button @click="handleCancel">返回列表</el-button>
          </div>
        </div>
      </template>

      <el-form
        ref="formRef"
        :model="formData"
        :rules="formRules"
        label-width="120px"
        class="form-content"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="返货类型" prop="returnType">
              <el-radio-group v-model="formData.returnType">
                <el-radio :label="1">退货</el-radio>
                <el-radio :label="3">返修</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="客户" prop="customerId">
              <el-input
                v-model="formData.customerName"
                placeholder="请选择客户"
                readonly
                @click="openCustomerPicker"
              >
                <template #suffix>
                  <el-icon v-if="formData.customerId" class="clear-icon" @click.stop="clearCustomer">
                    <Close />
                  </el-icon>
                  <el-icon class="arrow-icon">
                    <ArrowDown />
                  </el-icon>
                </template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="原销售订单号" prop="originalOrderNo">
              <el-input v-model="formData.originalOrderNo" placeholder="请输入原销售订单号" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="仓库" prop="warehouseId">
              <el-input
                v-model="formData.warehouseName"
                placeholder="请选择仓库"
                readonly
                @click="openWarehousePicker"
              >
                <template #suffix>
                  <el-icon v-if="formData.warehouseId" class="clear-icon" @click.stop="clearWarehouse">
                    <Close />
                  </el-icon>
                  <el-icon class="arrow-icon">
                    <ArrowDown />
                  </el-icon>
                </template>
              </el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="返货日期" prop="returnDate">
              <el-date-picker
                v-model="formData.returnDate"
                type="date"
                placeholder="请选择返货日期"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="返货金额(元)">
              <el-input-number
                v-model="formData.returnAmount"
                :min="0"
                :precision="2"
                :step="100"
                style="width: 100%"
                :disabled="true"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="返货原因" prop="returnReason">
          <el-input
            v-model="formData.returnReason"
            type="textarea"
            :rows="3"
            placeholder="请填写返货原因"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>

        <!-- 明细列表 -->
        <el-form-item label="返货明细" required>
          <div class="items-section">
            <div class="items-header">
              <span class="items-title">商品明细</span>
              <el-button type="primary" size="small" @click="addItem">新增明细</el-button>
            </div>
            <el-table :data="formData.items" border style="width: 100%">
              <el-table-column label="商品名称" min-width="180">
                <template #default="{ row, $index }">
                  <div class="product-cell">
                    <el-input
                      v-model="row.productName"
                      placeholder="请选择或输入商品名称"
                      class="product-input"
                    />
                    <el-button 
                      v-if="!isPersonalUser"
                      type="primary" 
                      link 
                      size="small" 
                      @click="openProductPicker($index)"
                    >
                      选择商品
                    </el-button>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="productSpec" label="规格" min-width="120">
                <template #default="{ row }">
                  <el-input v-model="row.productSpec" placeholder="规格" />
                </template>
              </el-table-column>
              <el-table-column prop="quantity" label="数量" width="150" align="right">
                <template #default="{ row }">
                  <el-input-number
                    v-model="row.quantity"
                    :min="1"
                    :step="1"
                    :controls-position="'right'"
                    style="width: 100%"
                    @change="recalcItemAmount(row)"
                  />
                </template>
              </el-table-column>
              <el-table-column prop="unitPrice" label="单价(元)" width="160" align="right">
                <template #default="{ row }">
                  <el-input-number
                    v-model="row.unitPrice"
                    :min="0"
                    :precision="2"
                    :step="10"
                    :disabled="!!row.productId"
                    :controls-position="'right'"
                    style="width: 100%"
                    @change="recalcItemAmount(row)"
                  />
                </template>
              </el-table-column>
              <el-table-column prop="returnAmount" label="金额(元)" width="140" align="right">
                <template #default="{ row }">
                  <span style="color: #f56c6c; font-weight: 600">
                    {{ formatMoney(row.returnAmount || 0) }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column prop="reasonDetail" label="原因说明" min-width="160">
                <template #default="{ row }">
                  <el-input v-model="row.reasonDetail" placeholder="可填写具体问题说明" />
                </template>
              </el-table-column>
              <el-table-column label="操作" width="100" fixed="right">
                <template #default="{ $index }">
                  <el-button type="danger" link size="small" @click="removeItem($index)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-form-item>

        <el-form-item label="备注">
          <el-input
            v-model="formData.remark"
            type="textarea"
            :rows="3"
            placeholder="请输入备注"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>

        <el-form-item label="附件">
          <AttachmentUpload 
            ref="returnAttachmentUploadRef"
            v-model="formData.attachments"
            :max-count="10"
            :business-type="'RETURN'"
            :module="'返货单'"
            :show-download="false"
          />
        </el-form-item>

        <el-form-item>
          <div class="form-footer">
            <template v-if="formData.returnType === 3">
              <el-button @click="handleCancel">取消</el-button>
              <el-button type="primary" :disabled="!isDraftEditable" @click="handleSubmit('draft')">保存</el-button>
            </template>
            <template v-else>
              <el-button type="info" :disabled="!isDraftEditable" @click="handleSubmit('draft')">保存草稿</el-button>
              <div style="position: relative; display: inline-block;">
                <div v-if="isPersonalUser" style="position: absolute; top: -20px; right: 0; font-size: 12px; color: #67c23a; white-space: nowrap;">
                  个人用户点提交审批直接通过
                </div>
                <el-button class="submit-approval-btn" :disabled="!isDraftEditable" @click="handleSubmit('submit')">提交审批</el-button>
              </div>
            </template>
          </div>
        </el-form-item>
      </el-form>

      <!-- 客户选择弹窗（复用合同表单样式思路，简化版） -->
      <el-dialog
        v-model="customerPickerVisible"
        title="选择客户"
        width="600px"
        :close-on-click-modal="false"
      >
        <div class="customer-picker-content">
          <div class="customer-picker-search">
            <el-input
              v-model="customerPickerKeyword"
              placeholder="客户姓名/电话"
              clearable
              @keyup.enter="loadCustomerList"
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
            </el-input>
            <el-button type="primary" @click="loadCustomerList">搜索</el-button>
          </div>
          <div class="customer-picker-list" v-loading="customerPickerLoading">
            <div
              v-for="item in customerPickerList"
              :key="item.id"
              class="customer-picker-item"
              :class="{ active: formData.customerId === item.id }"
              @click="selectCustomer(item)"
            >
              <div class="customer-item-main">
                <div class="customer-name">{{ item.customerName }}</div>
                <div class="customer-meta">
                  <span v-if="item.contactName">{{ item.contactName }}</span>
                  <span v-else class="no-contact">暂无联系人</span>
                  <span v-if="item.contactPhone" class="phone">{{ item.contactPhone }}</span>
                </div>
              </div>
              <el-icon v-if="formData.customerId === item.id" class="check-icon">
                <Check />
              </el-icon>
            </div>
            <el-empty
              v-if="!customerPickerLoading && customerPickerList.length === 0"
              description="暂无客户数据"
              :image-size="100"
            />
          </div>
          <div class="customer-picker-pagination" v-if="customerPickerTotal > 0">
            <el-pagination
              v-model:current-page="customerPickerPage"
              v-model:page-size="customerPickerPageSize"
              :total="customerPickerTotal"
              :page-sizes="[10, 20, 50, 100]"
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleCustomerPageSizeChange"
              @current-change="handleCustomerPageChange"
            />
          </div>
        </div>
      </el-dialog>

      <!-- 仓库选择弹窗 -->
      <el-dialog
        v-model="warehousePickerVisible"
        title="选择仓库"
        width="600px"
        :close-on-click-modal="false"
      >
        <div class="warehouse-picker-content">
          <div class="warehouse-picker-search">
            <el-input
              v-model="warehousePickerKeyword"
              placeholder="仓库名称/编码"
              clearable
              @keyup.enter="loadWarehouseList"
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
            </el-input>
            <el-button type="primary" @click="loadWarehouseList">搜索</el-button>
          </div>
          <el-table
            :data="warehousePickerList"
            border
            height="360"
            v-loading="warehousePickerLoading"
            @row-click="selectWarehouse"
          >
            <el-table-column prop="warehouseName" label="仓库名称" />
            <el-table-column prop="warehouseCode" label="编码" width="140" />
            <el-table-column prop="address" label="地址" />
          </el-table>
          <div class="warehouse-picker-pagination" v-if="warehousePickerTotal > 0">
            <el-pagination
              v-model:current-page="warehousePickerPage"
              v-model:page-size="warehousePickerPageSize"
              :total="warehousePickerTotal"
              :page-sizes="[10, 20, 50, 100]"
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleWarehousePageSizeChange"
              @current-change="handleWarehousePageChange"
            />
          </div>
        </div>
      </el-dialog>

      <!-- 商品选择弹窗 -->
      <el-dialog
        v-model="productPickerVisible"
        title="选择商品"
        width="800px"
        :close-on-click-modal="false"
      >
        <div class="product-picker-content">
          <div class="product-picker-search">
            <el-input
              v-model="productPickerKeyword"
              placeholder="商品名称/编码"
              clearable
              @keyup.enter="loadProductList"
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
            </el-input>
            <el-button type="primary" @click="loadProductList">搜索</el-button>
          </div>
          <el-table
            :data="productPickerList"
            border
            height="360"
            v-loading="productPickerLoading"
            @row-click="selectProduct"
          >
            <el-table-column prop="productName" label="商品名称" min-width="180" />
            <el-table-column prop="productCode" label="商品编码" width="140" />
            <el-table-column prop="spec" label="规格" width="140" />
            <el-table-column prop="warehouseName" label="所属仓库" width="140" />
            <el-table-column prop="stock" label="库存" width="100" align="right">
              <template #default="{ row }">
                {{ getProductStock(row) }}
              </template>
            </el-table-column>
            <el-table-column prop="unitPrice" label="标准单价(元)" width="140" align="right">
              <template #default="{ row }">
                {{ formatMoney(getProductUnitPrice(row)) }}
              </template>
            </el-table-column>
          </el-table>
          <div class="product-picker-pagination" v-if="productPickerTotal > 0">
            <el-pagination
              v-model:current-page="productPickerPage"
              v-model:page-size="productPickerPageSize"
              :total="productPickerTotal"
              :page-sizes="[10, 20, 50, 100]"
              layout="total, sizes, prev, pager, next, jumper"
              @size-change="handleProductPageSizeChange"
              @current-change="handleProductPageChange"
            />
          </div>
        </div>
      </el-dialog>
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
  getCustomerList,
  getReturnOrderDetail,
  createReturnOrder,
  updateReturnOrder,
  submitReturnOrder,
  getWarehouseListForSalesman,
  getProductListForSalesman
} from '../api/salesman'
import { Close, ArrowDown, Search, Check } from '@element-plus/icons-vue'
import { getFileName } from '../api/file'
import AttachmentUpload from '../components/AttachmentUpload.vue'

const route = useRoute()
const router = useRouter()
const closeTab = inject('closeTab', null)

const formRef = ref(null)
const returnAttachmentUploadRef = ref(null)
const isEdit = computed(() => !!route.query.id && !route.query.correctId)
const isCorrectMode = computed(() => !!route.query.correctId)
const correctId = computed(() => route.query.correctId ? Number(route.query.correctId) : null)

const formData = reactive({
  id: null,
  status: 'draft',
  customerId: null,
  customerName: '',
  originalOrderNo: '',
  returnType: 1,
  returnReason: '',
  returnDate: '',
  expectedHandleType: 1,
  returnAmount: 0,
  refundAmount: 0,
  warehouseId: null,
  warehouseName: '',
  remark: '',
  attachments: [], // 附件列表，格式：[{url: string, fileName: string, size: number}]
  repairVendor: '',
  items: []
})

const formRules = {
  customerId: [{ required: true, message: '请选择客户', trigger: 'change' }],
  returnType: [{ required: true, message: '请选择返货类型', trigger: 'change' }],
  returnReason: [{ required: true, message: '请输入返货原因', trigger: 'blur' }],
  returnDate: [{ required: true, message: '请选择返货日期', trigger: 'change' }]
  // 仓库字段已改为非必填
}

// 根据返货类型自动设置处理方式：退货=退货退款(1)，返修=返修处理(3)
watch(
  () => formData.returnType,
  (val) => {
    formData.expectedHandleType = val === 1 ? 1 : 3
  },
  { immediate: true }
)

// 当前是否可编辑：草稿状态 或 修正模式
const isDraftEditable = computed(() => {
  // 修正模式下，允许编辑（用于已驳回单据的重新提交）
  if (isCorrectMode.value) return true
  if (!formData.status) return true
  return String(formData.status).toLowerCase() === 'draft'
})

// 提交状态
const submitting = ref(false)

const formatMoney = (amount) => {
  if (!amount) return '0.00'
  return Number(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// 获取企业ID（用于接口过滤）
const getCompanyId = () => {
  try {
    const userInfoStr = localStorage.getItem('userInfo')
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr)
      return userInfo.companyId || null
    }
  } catch (e) {
    console.warn('解析用户信息失败', e)
  }
  return null
}

// 判断是否是个人用户（没有企业ID）
const isPersonalUser = computed(() => {
  return !getCompanyId()
})

// 商品标准单价字段兼容处理
const getProductUnitPrice = (row) => {
  if (!row) return 0
  // 后端可能返回 unitPrice / standardPrice / price 等字段，做一次兼容
  const price =
    row.unitPrice ?? // PC 端或后端直接给的单价
    row.salePrice ?? // 小程序商品列表里的销售价
    row.costPrice ?? // 小程序里兜底成本价
    row.standardPrice ??
    row.price ??
    row.standardPriceAmount ??
    0
  return Number(price || 0)
}

// 商品库存字段兼容处理
const getProductStock = (row) => {
  if (!row) return 0
  const stock =
    row.stock ?? // 小程序 productList 里使用的字段
    row.inventory ??
    row.availableStock ??
    row.quantity ??
    0
  return Number(stock || 0)
}

// 明细操作
const addItem = () => {
  // 校验上一条明细是否填写完整（名称、数量、单价、金额）
  if (formData.items.length > 0) {
    const last = formData.items[formData.items.length - 1]
    const hasName = last.productName && last.productName.trim() !== ''
    const hasQty = Number(last.quantity || 0) > 0
    const hasPrice = Number(last.unitPrice || 0) > 0
    const hasAmount = Number(last.returnAmount || 0) > 0
    if (!hasName || !hasQty || !hasPrice || !hasAmount) {
      ElMessage.warning('请先完善当前明细的名称、数量、单价、金额')
      return
    }
  }

  formData.items.push({
    id: null,
    productId: null,
    productName: '',
    productSpec: '',
    originalOrderItemId: null,
    quantity: 1,
    unitPrice: 0,
    returnAmount: 0,
    reasonDetail: '',
    warehouseLocation: ''
  })
}

const removeItem = (index) => {
  formData.items.splice(index, 1)
  recalcTotalAmount()
}

const recalcItemAmount = (item) => {
  const qty = Number(item.quantity || 0)
  const price = Number(item.unitPrice || 0)
  item.returnAmount = qty * price
  recalcTotalAmount()
}

const recalcTotalAmount = () => {
  const total = formData.items.reduce((sum, item) => {
    return sum + Number(item.returnAmount || 0)
  }, 0)
  formData.returnAmount = total
}

// 客户选择
const customerPickerVisible = ref(false)
const customerPickerKeyword = ref('')
const customerPickerLoading = ref(false)
const customerPickerList = ref([])
const customerPickerPage = ref(1)
const customerPickerPageSize = ref(10)
const customerPickerTotal = ref(0)

const openCustomerPicker = () => {
  customerPickerVisible.value = true
  loadCustomerList()
}

const clearCustomer = () => {
  formData.customerId = null
  formData.customerName = ''
}

const loadCustomerList = async () => {
  customerPickerLoading.value = true
  try {
    const res = await getCustomerList({
      page: customerPickerPage.value,
      pageSize: customerPickerPageSize.value,
      keyword: customerPickerKeyword.value
    })
    if (res.code === '000000') {
      customerPickerList.value = res.data?.list || res.data?.records || []
      customerPickerTotal.value = res.data?.total || 0
    }
  } catch (error) {
    console.error('获取客户列表失败', error)
    ElMessage.error('获取客户列表失败')
  } finally {
    customerPickerLoading.value = false
  }
}

const selectCustomer = (item) => {
  formData.customerId = item.id
  formData.customerName = item.customerName
  customerPickerVisible.value = false
}

const handleCustomerPageSizeChange = (val) => {
  customerPickerPageSize.value = val
  customerPickerPage.value = 1
  loadCustomerList()
}

const handleCustomerPageChange = (val) => {
  customerPickerPage.value = val
  loadCustomerList()
}

// 仓库选择
const warehousePickerVisible = ref(false)
const warehousePickerKeyword = ref('')
const warehousePickerLoading = ref(false)
const warehousePickerList = ref([])
const warehousePickerPage = ref(1)
const warehousePickerPageSize = ref(10)
const warehousePickerTotal = ref(0)

const openWarehousePicker = () => {
  warehousePickerVisible.value = true
  loadWarehouseList()
}

const clearWarehouse = () => {
  formData.warehouseId = null
  formData.warehouseName = ''
  // 清空商品明细的商品关联信息
  formData.items = formData.items.map(item => ({
    ...item,
    productId: null,
    productName: '',
    productSpec: ''
  }))
}

const loadWarehouseList = async () => {
  warehousePickerLoading.value = true
  try {
    const companyId = getCompanyId()
    const res = await getWarehouseListForSalesman({
      page: warehousePickerPage.value,
      pageSize: warehousePickerPageSize.value,
      keyword: warehousePickerKeyword.value,
      companyId: companyId || undefined
    })
    if (res.code === '000000') {
      warehousePickerList.value = res.data?.list || res.data?.records || []
      warehousePickerTotal.value = res.data?.total || 0
    }
  } catch (error) {
    console.error('获取仓库列表失败', error)
    ElMessage.error('获取仓库列表失败')
  } finally {
    warehousePickerLoading.value = false
  }
}

const selectWarehouse = (row) => {
  const oldId = formData.warehouseId
  formData.warehouseId = row.id
  formData.warehouseName = row.warehouseName
  warehousePickerVisible.value = false
  // 如果仓库发生变化，清空已选商品信息
  if (oldId && oldId !== row.id) {
    formData.items = formData.items.map(item => ({
      ...item,
      productId: null,
      productName: '',
      productSpec: ''
    }))
    recalcTotalAmount()
  }
}

const handleWarehousePageSizeChange = (val) => {
  warehousePickerPageSize.value = val
  warehousePickerPage.value = 1
  loadWarehouseList()
}

const handleWarehousePageChange = (val) => {
  warehousePickerPage.value = val
  loadWarehouseList()
}

// 商品选择
const productPickerVisible = ref(false)
const productPickerKeyword = ref('')
const productPickerLoading = ref(false)
const productPickerList = ref([])
const productPickerPage = ref(1)
const productPickerPageSize = ref(10)
const productPickerTotal = ref(0)
const currentProductRowIndex = ref(-1)

const openProductPicker = (rowIndex) => {
  // 业务规则：商品基于仓库，必须先选择仓库
  if (!formData.warehouseId) {
    ElMessage.warning('请先选择仓库')
    return
  }
  currentProductRowIndex.value = rowIndex
  productPickerVisible.value = true
  loadProductList()
}

const loadProductList = async () => {
  productPickerLoading.value = true
  try {
    const companyId = getCompanyId()
    const res = await getProductListForSalesman({
      page: productPickerPage.value,
      pageSize: productPickerPageSize.value,
      keyword: productPickerKeyword.value || undefined,
      // 按仓库过滤商品列表（与小程序保持一致）
      warehouseId: formData.warehouseId || undefined,
      companyId: companyId || undefined
    })
    if (res.code === '000000') {
      productPickerList.value = res.data?.list || res.data?.records || []
      productPickerTotal.value = res.data?.total || 0
    }
  } catch (error) {
    console.error('获取商品列表失败', error)
    ElMessage.error('获取商品列表失败')
  } finally {
    productPickerLoading.value = false
  }
}

const selectProduct = (row) => {
  const index = currentProductRowIndex.value
  if (index >= 0 && index < formData.items.length) {
    const item = formData.items[index]
    item.productId = row.id
    item.productName = row.productName
    item.productSpec = row.spec
    // 如果当前明细未填写单价，则默认带入商品标准单价
    if (!item.unitPrice) {
      const unitPrice = getProductUnitPrice(row)
      if (unitPrice) {
        item.unitPrice = unitPrice
        recalcItemAmount(item)
      }
    }
    // 从商品列表选择后，金额按数量*单价自动计算
    recalcItemAmount(item)
  }
  productPickerVisible.value = false
}

const handleProductPageSizeChange = (val) => {
  productPickerPageSize.value = val
  productPickerPage.value = 1
  loadProductList()
}

const handleProductPageChange = (val) => {
  productPickerPage.value = val
  loadProductList()
}

// 提交 / 保存
const handleSubmit = (mode) => {
  if (!formRef.value) return
  if (!isDraftEditable.value) {
    ElMessage.error('仅草稿状态可以编辑')
    return
  }
  
  // 按顺序校验：先校验表单必填项（客户、返货类型、返货原因、返货日期等）
  formRef.value.validate(async (valid) => {
    if (!valid) return
    
    // 然后检查是否有明细
    if (!formData.items || formData.items.length === 0) {
      ElMessage.warning('请至少添加一条返货明细')
      return
    }
    
    // 最后校验商品明细必填字段
    for (let i = 0; i < formData.items.length; i++) {
      const item = formData.items[i]
      if (!item.productName || item.productName.trim() === '') {
        ElMessage.warning(`第${i + 1}条明细的商品名称不能为空`)
        return
      }
      if (!item.quantity || Number(item.quantity) <= 0) {
        ElMessage.warning(`第${i + 1}条明细的数量必须大于0`)
        return
      }
      if (!item.unitPrice || Number(item.unitPrice) <= 0) {
        ElMessage.warning(`第${i + 1}条明细的单价必须大于0`)
        return
      }
    }
    submitting.value = true
    try {
      // 使用附件上传组件的统一上传逻辑（带进度条）
      if (returnAttachmentUploadRef.value) {
        try {
          const uploadedAttachments = await returnAttachmentUploadRef.value.uploadAll()
          formData.attachments = uploadedAttachments || formData.attachments
        } catch (error) {
          ElMessage.error(`附件上传失败: ${error.message}`)
          throw error
        }
      }
      
      const payload = JSON.parse(JSON.stringify(formData))
      // 修正模式下创建新数据
      // 处理附件数据，转换为URL数组（后端期望List<String>）
      payload.attachments = formData.attachments && formData.attachments.length
        ? formData.attachments.map(att => att.url || '').filter(url => url)
        : []
      
      const api = (isEdit.value && !isCorrectMode.value) ? updateReturnOrder : createReturnOrder
      const res = await api(payload)
      if (res.code === '000000') {
        if (!isEdit.value && res.data) {
          formData.id = res.data
        }
        
        // 构建当前标签页的路径（用于关闭标签页）
        // 注意：与 Home.vue 中 addTab 对返货单表单使用的 tabKey 保持一致
        // - 编辑模式：/home/salesman-return-order-form?id=xxx
        // - 新增/修正模式：/home/salesman-return-order-form
        let fullPath = route.path
        if (route.path === '/home/salesman-return-order-form' && route.query && route.query.id) {
          fullPath = `${route.path}?id=${route.query.id}`
        } else if (route.path === '/home/salesman-return-order-form') {
          fullPath = route.path
        } else if (route.query && Object.keys(route.query).length > 0) {
          const queryString = Object.keys(route.query)
            .map(key => `${key}=${encodeURIComponent(route.query[key])}`)
            .join('&')
          fullPath = `${route.path}?${queryString}`
        }
        
        if (mode === 'submit') {
          await submitReturnOrder({ id: formData.id || res.data })
          ElMessage.success('提交成功')
          // 提交审批成功后，个人用户跳转到已通过状态，其他用户跳转到待审批状态
          const status = isPersonalUser.value ? 'approved' : 'pending'
          router.push({ 
            name: 'SalesmanReturnOrderManagement', 
            query: { status: status } 
          }).finally(() => {
            if (closeTab) {
              closeTab(fullPath)
            }
          })
        } else {
          ElMessage.success('保存成功')
          // 保存草稿成功后，跳转到草稿状态列表，并关闭当前标签页
          router.push({ 
            name: 'SalesmanReturnOrderManagement', 
            query: { status: 'draft' } 
          }).finally(() => {
            if (closeTab) {
              closeTab(fullPath)
            }
          })
        }
      }
    } catch (error) {
      console.error('保存返货单失败', error)
      ElMessage.error('保存返货单失败')
    } finally {
      submitting.value = false
    }
  })
}

const handleCancel = () => {
  // 与提交时保持一致的标签路径构造逻辑
  let fullPath = route.path
  if (route.path === '/home/salesman-return-order-form' && route.query && route.query.id) {
    fullPath = `${route.path}?id=${route.query.id}`
  } else if (route.path === '/home/salesman-return-order-form') {
    fullPath = route.path
  } else if (route.query && Object.keys(route.query).length > 0) {
    const queryString = Object.keys(route.query)
      .map(key => `${key}=${encodeURIComponent(route.query[key])}`)
      .join('&')
    fullPath = `${route.path}?${queryString}`
  }

  const targetRoute = { name: 'SalesmanReturnOrderManagement' }

  router.push(targetRoute).finally(() => {
    if (closeTab) {
      closeTab(fullPath)
    }
  })
}

// 加载详情（编辑或修正）
const loadDetail = async () => {
  // 修正模式使用correctId，编辑模式使用id
  const id = isCorrectMode.value ? correctId.value : (route.query.id ? Number(route.query.id) : null)
  if (!id) {
    addItem()
    return
  }
  try {
    const res = await getReturnOrderDetail(id)
    if (res.code === '000000' && res.data) {
      const data = res.data
      
      // 处理附件字段
      const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
      
      // 处理附件数据（优先使用后端返回的附件信息，包含文件大小）
      let attachments = []
      // 优先使用 attachmentInfos（包含文件大小信息）
      if (data.attachmentInfos && Array.isArray(data.attachmentInfos) && data.attachmentInfos.length > 0) {
        attachments = data.attachmentInfos.map(att => {
          let url = att.fileUrl || att.url || att
          if (typeof url === 'string' && !url.startsWith('http://') && !url.startsWith('https://')) {
            url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
          }
          return {
            url,
            fileName: att.fileName || url.split('/').pop() || '附件',
            originalFileName: att.originalFileName || att.fileName || url.split('/').pop() || '附件',
            size: att.size || 0
          }
        })
      } else if (data.attachments) {
        if (Array.isArray(data.attachments)) {
          attachments = await Promise.all(data.attachments.map(async (att) => {
            if (typeof att === 'string') {
              let url = att
              if (!url.startsWith('http://') && !url.startsWith('https://')) {
                url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
              }
              // 尝试从后端获取原始文件名和文件大小
              let originalFileName = url.split('/').pop() || '附件'
              let fileSize = 0
              try {
                const fileNameRes = await getFileName(url)
                if (fileNameRes && fileNameRes.code === '000000') {
                  originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
                }
              } catch (error) {
                console.debug('获取文件名失败:', error)
              }
              // 尝试通过 HEAD 请求获取文件大小
              try {
                const response = await fetch(url, { method: 'HEAD' })
                if (response.ok) {
                  const contentLength = response.headers.get('Content-Length')
                  if (contentLength) {
                    fileSize = parseInt(contentLength, 10) || 0
                  }
                }
              } catch (error) {
                console.debug('获取文件大小失败:', error)
              }
              return { 
                url, 
                fileName: originalFileName, // 使用原始文件名
                originalFileName, // 保存原始文件名
                size: fileSize 
              }
            }
            return att
          }))
        } else if (typeof data.attachments === 'string') {
          try {
            const parsed = JSON.parse(data.attachments)
            const urls = Array.isArray(parsed) ? parsed : [parsed]
            attachments = await Promise.all(urls.map(async (url) => {
              if (typeof url === 'string') {
                let fullUrl = url
                if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
                  fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
                }
                // 尝试从后端获取原始文件名和文件大小
                let originalFileName = fullUrl.split('/').pop() || '附件'
                let fileSize = 0
                try {
                  const fileNameRes = await getFileName(fullUrl)
                  if (fileNameRes && fileNameRes.code === '000000') {
                    originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
                  }
                } catch (error) {
                  console.debug('获取文件名失败:', error)
                }
                // 尝试通过 HEAD 请求获取文件大小
                try {
                  const response = await fetch(fullUrl, { method: 'HEAD' })
                  if (response.ok) {
                    const contentLength = response.headers.get('Content-Length')
                    if (contentLength) {
                      fileSize = parseInt(contentLength, 10) || 0
                    }
                  }
                } catch (error) {
                  console.debug('获取文件大小失败:', error)
                }
                return { 
                  url: fullUrl, 
                  fileName: originalFileName, // 使用原始文件名
                  originalFileName, // 保存原始文件名
                  size: fileSize 
                }
              }
              return url
            }))
          } catch {
            const urlArray = data.attachments.split(',').map(s => s.trim()).filter(Boolean)
            attachments = await Promise.all(urlArray.map(async (url) => {
              let fullUrl = url
              if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
                fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
              }
              // 尝试从后端获取原始文件名
              let originalFileName = fullUrl.split('/').pop() || '附件'
              try {
                const fileNameRes = await getFileName(fullUrl)
                if (fileNameRes && fileNameRes.code === '000000') {
                  originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
                }
              } catch (error) {
                console.debug('获取文件名失败:', error)
              }
              return { 
                url: fullUrl, 
                fileName: originalFileName, // 使用原始文件名
                originalFileName, // 保存原始文件名
                size: 0 
              }
            }))
          }
        }
      }
      
      Object.assign(formData, {
        ...formData,
        ...data,
        id: isCorrectMode.value ? null : data.id, // 修正模式下不设置id，创建新数据
        attachments
      })
      formData.items = data.items || []
      // 重新根据明细计算总返货金额，避免顶部金额与明细不一致
      recalcTotalAmount()

      // 详情加载后，如果不是草稿状态且不是修正模式，提示并禁用编辑
      if (!isDraftEditable.value && !isCorrectMode.value) {
        ElMessage.warning('当前返货单已提交或审核中，无法编辑，仅可查看')
      }
    }
  } catch (error) {
    console.error('获取返货单详情失败', error)
    ElMessage.error('获取返货单详情失败')
  }
}

onMounted(() => {
  loadDetail()
})
</script>

<style scoped>
.return-order-form {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.form-content {
  margin-top: 10px;
}

.form-footer {
  display: flex;
  gap: 12px;
}

.clear-icon {
  cursor: pointer;
  margin-right: 4px;
}

.arrow-icon {
  color: #c0c4cc;
}

.items-section {
  width: 100%;
}

.items-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.items-title {
  font-weight: 600;
}

.product-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.product-input {
  flex: 1;
}

/* 确保表格中的输入框和按钮不被遮挡 */
.items-section :deep(.el-table) {
  overflow-x: auto;
}

.attachment-upload-container {
  width: 100%;
}

.attachment-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 10px;
}

.attachment-item {
  position: relative;
  display: inline-block;
}

.attachment-item .attachment-actions {
  position: absolute;
  top: -8px;
  right: -8px;
}

.attachment-file {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background: #f5f7fa;
}

.attachment-file .el-icon {
  margin-right: 8px;
  color: #409eff;
}

.attachment-name {
  font-size: 12px;
  color: #606266;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-uploader {
  display: inline-block;
}

.add-image-btn {
  width: 80px;
  height: 80px;
  border: 1px dashed #d9d9d9;
  background-color: #fafafa;
  color: #8c8c8c;
  transition: all 0.3s;
}

.add-image-btn:hover {
  border-color: #409eff;
  color: #409eff;
  background-color: #f0f9ff;
}

.upload-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.8;
}

.upload-tip .tip-item {
  margin: 2px 0;
}

.items-section :deep(.el-table .el-input-number) {
  width: 100%;
}

.items-section :deep(.el-table .el-input-number__decrease),
.items-section :deep(.el-table .el-input-number__increase) {
  width: 22px;
  height: auto;
  line-height: 1;
}

.customer-picker-content,
.warehouse-picker-content,
.product-picker-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.customer-picker-search,
.warehouse-picker-search,
.product-picker-search {
  display: flex;
  gap: 8px;
}

.customer-picker-list {
  max-height: 360px;
  overflow-y: auto;
  margin-top: 8px;
}

.customer-picker-item {
  display: flex;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.customer-picker-item:hover {
  background-color: #f5f7fa;
}

.customer-picker-item.active {
  background-color: #ecf5ff;
}

.customer-name {
  font-weight: 600;
}

.customer-meta {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  display: flex;
  gap: 8px;
}

.customer-meta .phone {
  color: #409eff;
}

.check-icon {
  color: #67c23a;
}

.customer-picker-pagination,
.warehouse-picker-pagination,
.product-picker-pagination {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
}

/* 提交审批按钮淡蓝色样式 */
.submit-approval-btn {
  background-color: #5B9BD5;
  border-color: #5B9BD5;
  color: #fff;
}

.submit-approval-btn:hover {
  background-color: #4A8BC2;
  border-color: #4A8BC2;
}

.submit-approval-btn:disabled {
  background-color: #C0C4CC;
  border-color: #C0C4CC;
  color: #fff;
}
</style>



